import { useCallback, useState } from "react";
import {
  useConfirmHandler,
  type ConfirmHandlerOptions,
  type UseConfirmHandlerResult,
} from "../../../../hooks/useConfirmHandler";
import type { ProgressCount } from "../../../../components/primitives/feedback/ProgressBar";
import type { Selection } from "../../../../hooks/useSelection";
import { applyBatchJobItem, dismissBatchJobItem } from "../../../../lib/corpusApi";

/** Which way the selected items are settled: applying their finding, or dismissing it. */
export type BulkReviewDecision = "apply" | "dismiss";

export interface UseBulkItemReviewOptions {
  jobId: string;
  decision: BulkReviewDecision;
  selection: Selection;
  /** Refetches the list once the run is over, whatever its outcome. */
  refresh: () => Promise<void>;
  /** The confirmation shown before the run; its message names how many items are selected. */
  confirm: ConfirmHandlerOptions;
  /** What a failed item failed to do, for the error shown when part of the run failed. */
  failureVerb: string;
}

export interface UseBulkItemReviewResult {
  open: () => void;
  dialogProps: UseConfirmHandlerResult["dialogProps"];
  /** Items settled so far, failed ones included. Non-null from the moment the run starts until it ends. */
  progress: ProgressCount | null;
}

/** Settles the selected items of a batch job one after another behind a confirmation, reporting
 *  progress as it goes. The items that failed stay selected so a retry only tries those, and the
 *  dialog stays open on the error; a clean run clears the selection. */
export function useBulkItemReview({
  jobId,
  decision,
  selection,
  refresh,
  confirm,
  failureVerb,
}: UseBulkItemReviewOptions): UseBulkItemReviewResult {
  const { selectedIds, unselectAll, selectOnly } = selection;
  const [progress, setProgress] = useState<ProgressCount | null>(null);
  const confirmHandler = useConfirmHandler(confirm);

  const open = useCallback(() => {
    confirmHandler.handle(async () => {
      const ids = Array.from(selectedIds);
      const failures: string[] = [];
      setProgress({ done: 0, total: ids.length });
      try {
        for (const [index, id] of ids.entries()) {
          try {
            await (decision === "apply" ? applyBatchJobItem : dismissBatchJobItem)(jobId, id);
          } catch {
            failures.push(id);
          }
          setProgress({ done: index + 1, total: ids.length });
        }
        await refresh();
      } finally {
        setProgress(null);
      }
      if (failures.length === 0) {
        unselectAll();
        return;
      }
      selectOnly(failures);
      throw new Error(
        `${failures.length} of ${ids.length} item(s) failed to ${failureVerb} — the rest succeeded. Retry to try the remaining ones again.`
      );
    });
  }, [confirmHandler, jobId, decision, refresh, selectedIds, unselectAll, selectOnly, failureVerb]);

  return { open, dialogProps: confirmHandler.dialogProps, progress };
}
